import React, { useMemo } from "react";

export default function VisualAnalyticsRow({
  reportsList = [],
  testCaseRepo = {},
  manualCases = [],
  scenarioBugsList = []
}) {
  // -------------------------------------------------------------
  // 1. EXECUTION VELOCITY (Bump/Slope Chart)
  // -------------------------------------------------------------
  const executionVelocity = useMemo(() => {
    // Sort chronologically (oldest to newest), taking up to the last 5 runs
    const runs = [...reportsList].reverse().slice(-5);

    if (runs.length === 0) {
      return {
        points: "20,40 70,40 120,40 170,40 220,40",
        dots: [
          { cx: 20, cy: 40, fill: "#94a3b8", label: "R-1", title: "No data" },
          { cx: 70, cy: 40, fill: "#94a3b8", label: "R-2", title: "No data" },
          { cx: 120, cy: 40, fill: "#94a3b8", label: "R-3", title: "No data" },
          { cx: 170, cy: 40, fill: "#94a3b8", label: "R-4", title: "No data" },
          { cx: 220, cy: 40, fill: "#94a3b8", label: "R-5", title: "No data" }
        ],
        labels: ["R-1", "R-2", "R-3", "R-4", "R-5"],
        quote: "Awaiting test executions to compute velocity"
      };
    }

    const n = runs.length;
    const padding = 25;
    const width = 240 - 2 * padding;
    const dots = [];
    const pts = [];
    const labels = [];
    let lowestRate = 101;
    let dipIndex = -1;
    let passedCount = 0;

    runs.forEach((r, idx) => {
      const status = (r.status || "").toLowerCase();
      const isPassed = status === "passed";
      const isFailed = status === "failed" || status === "error";
      if (isPassed) passedCount++;

      const rate = isPassed ? 100 : (isFailed ? 0 : 50);
      if (rate < lowestRate) {
        lowestRate = rate;
        dipIndex = idx;
      }

      const x = n === 1 ? 120 : Math.round(padding + (idx / (n - 1)) * width);
      // y: 100% -> 12, 0% -> 68 (viewBox 0 0 240 80)
      const y = Math.round(12 + (1 - (rate / 100)) * 54);
      const fill = isPassed ? "#86efac" : (isFailed ? "#f87171" : "#facc15");
      const label = `R-${idx + 1}`;
      const title = `${r.run_id || label}: ${r.status || "Unknown"} (${rate}%)`;

      labels.push(label);
      dots.push({ cx: x, cy: y, fill, label, title, rate, runId: r.run_id });
      pts.push(`${x},${y}`);
    });

    if (n === 1) {
      pts.unshift(`20,${dots[0].cy}`);
      pts.push(`220,${dots[0].cy}`);
    }

    let quote = "All test runs executing at 100% velocity";
    if (lowestRate < 100 && dipIndex >= 0) {
      const dipLabel = `R-${dipIndex + 1}`;
      const latestRate = dots[dots.length - 1].rate;
      if (latestRate === 100) {
        quote = `${dipLabel} was the dip (${lowestRate}%), latest run recovered to 100%`;
      } else {
        quote = `${dipLabel} dipped to ${lowestRate}%, ${passedCount}/${n} passing overall`;
      }
    } else if (passedCount === n) {
      quote = "100% pass velocity maintained across all recent runs";
    }

    return {
      points: pts.join(" "),
      dots,
      labels,
      quote
    };
  }, [reportsList]);

  // -------------------------------------------------------------
  // 2. FAILURE ROOT CAUSES (Tag Cloud)
  // -------------------------------------------------------------
  const failureRootCauses = useMemo(() => {
    const categories = {
      "Locator Timeout": 0,
      "Assertion Error": 0,
      "Network Error": 0,
      "Stale Element": 0,
      "Script Error": 0
    };

    let totalFailures = 0;
    reportsList.forEach(r => {
      const status = (r.status || "").toLowerCase();
      if (status !== "passed") {
        totalFailures++;
        const text = `${r.output || ""} ${r.error || ""} ${r.test_case || ""} ${r.type || ""}`.toLowerCase();
        let matched = false;

        if (text.includes("timeout") || text.includes("waiting for locator") || text.includes("timed out") || text.includes("element not found")) {
          categories["Locator Timeout"]++;
          matched = true;
        }
        if (text.includes("assertion") || text.includes("assert") || text.includes("expect")) {
          categories["Assertion Error"]++;
          matched = true;
        }
        if (text.includes("net::") || text.includes("connection") || text.includes("500") || text.includes("404") || text.includes("network")) {
          categories["Network Error"]++;
          matched = true;
        }
        if (text.includes("stale") || text.includes("detached")) {
          categories["Stale Element"]++;
          matched = true;
        }
        if (text.includes("syntaxerror") || text.includes("typeerror") || text.includes("attributeerror") || text.includes("exception")) {
          categories["Script Error"]++;
          matched = true;
        }
        if (!matched) {
          categories["Locator Timeout"]++;
        }
      }
    });

    if (scenarioBugsList && scenarioBugsList.length > 0) {
      scenarioBugsList.forEach(bug => {
        const bText = (bug.title || "").toLowerCase();
        if (bText.includes("locator") || bText.includes("timeout")) categories["Locator Timeout"]++;
        else if (bText.includes("assert") || bText.includes("verification")) categories["Assertion Error"]++;
        else if (bText.includes("network") || bText.includes("api")) categories["Network Error"]++;
        else categories["Locator Timeout"]++;
        totalFailures++;
      });
    }

    const items = Object.entries(categories)
      .filter(([_, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);

    const maxCount = items.length > 0 ? items[0][1] : 1;

    return {
      totalFailures,
      items: items.map(([name, count]) => ({
        name,
        count,
        fontSize: Math.min(32, Math.max(14, 14 + Math.round((count / maxCount) * 16))),
        color: count >= maxCount ? "#e07a5f" : (count > 1 ? "#ea580c" : "#94a3b8")
      })),
      caption: items.length > 0
        ? `Text size = issue count (${items.map(x => x.count).join(", ")})`
        : "Zero failure anomalies detected across active suites"
    };
  }, [reportsList, scenarioBugsList]);

  // -------------------------------------------------------------
  // 3. ENGINE LATENCY (Sparklines & Durations)
  // -------------------------------------------------------------
  const engineLatency = useMemo(() => {
    const parseDuration = (val) => {
      if (typeof val === "number") return val;
      if (!val) return 0;
      const clean = String(val).replace(/s$/i, '').trim();
      const num = parseFloat(clean);
      return isNaN(num) ? 0 : num;
    };

    const getEngineData = (engineName, filterFn) => {
      const matches = reportsList.filter(filterFn);
      const durations = matches
        .map(r => parseDuration(r.duration))
        .filter(d => d > 0 && d < 3600); // Exclude 0 and extreme outliers
      const count = durations.length;
      const avg = count > 0 ? (durations.reduce((a, b) => a + b, 0) / count) : 0;

      const recent = durations.slice(-6);
      let points = "0,16 100,16";
      if (recent.length > 1) {
        const min = Math.min(...recent);
        const max = Math.max(...recent);
        const range = max - min || 1;
        points = recent.map((d, i) => {
          const x = Math.round((i / (recent.length - 1)) * 100);
          const y = Math.round(26 - ((d - min) / range) * 18);
          return `${x},${y}`;
        }).join(" ");
      } else if (avg > 0) {
        points = "0,18 20,16 40,20 60,14 80,18 100,16";
      }

      return {
        name: engineName,
        count,
        avgStr: avg > 0 ? `${avg.toFixed(1)}s` : "—",
        avgVal: avg,
        points
      };
    };

    const chrome = getEngineData("Chrome", r => {
      const b = (r.browser || "").toLowerCase();
      const id = (r.run_id || "").toLowerCase();
      return b.includes("chrom") || id.includes("chrom") || (!b && !id.includes("firefox") && !id.includes("webkit"));
    });

    const firefox = getEngineData("Firefox", r => {
      const b = (r.browser || "").toLowerCase();
      const id = (r.run_id || "").toLowerCase();
      return b.includes("firefox") || id.includes("firefox");
    });

    const webkit = getEngineData("WebKit", r => {
      const b = (r.browser || "").toLowerCase();
      const id = (r.run_id || "").toLowerCase();
      return b.includes("webkit") || b.includes("safari") || id.includes("webkit");
    });

    let insight = "Chrome running fastest — low latency maintained";
    const activeEngines = [chrome, firefox, webkit].filter(e => e.avgVal > 0);
    if (activeEngines.length > 1) {
      activeEngines.sort((a, b) => a.avgVal - b.avgVal);
      insight = `${activeEngines[0].name} fastest at ${activeEngines[0].avgStr} — ${activeEngines[activeEngines.length - 1].name} average is ${activeEngines[activeEngines.length - 1].avgStr}`;
    } else if (activeEngines.length === 1) {
      insight = `${activeEngines[0].name} active with ${activeEngines[0].avgStr} average execution duration`;
    } else {
      insight = "Engine duration trends will populate as test runs execute";
    }

    return { chrome, firefox, webkit, insight };
  }, [reportsList]);

  // -------------------------------------------------------------
  // 4. SUITE AUTOMATION (Coverage Score & Segmented Bar)
  // -------------------------------------------------------------
  const suiteAutomation = useMemo(() => {
    const automatedCount = testCaseRepo
      ? Object.values(testCaseRepo).reduce((acc, list) => acc + (Array.isArray(list) ? list.length : 0), 0)
      : 0;
    const manualCount = Array.isArray(manualCases) ? manualCases.length : 0;
    const untestedCount = 0;
    const total = automatedCount + manualCount + untestedCount;

    let automatedPct = 100;
    let manualPct = 0;
    let untestedPct = 0;

    if (total > 0) {
      automatedPct = Math.round((automatedCount / total) * 100);
      manualPct = Math.round((manualCount / total) * 100);
      untestedPct = Math.max(0, 100 - automatedPct - manualPct);
    }

    return {
      automatedCount,
      manualCount,
      untestedCount,
      automatedPct,
      manualPct,
      untestedPct,
      total
    };
  }, [testCaseRepo, manualCases]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "24px", width: "100%" }}>
      {/* Chart 1: Execution Velocity (Bump/Slope Chart) */}
      <div style={{ background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "12px", padding: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }} aria-label="Execution velocity pass rate trend chart">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "800", color: "#0f172a" }}> Execution Velocity</h4>
            <span style={{ fontSize: "11px", color: "#64748b" }}>Pass rate % per run</span>
          </div>
          <span style={{ fontSize: "10px", background: "#f0fdf4", color: "#166534", fontWeight: "800", padding: "2px 8px", borderRadius: "10px" }}>LIVE</span>
        </div>

        {/* Connected Line Chart */}
        <div style={{ position: "relative", height: "110px", width: "100%", marginTop: "4px" }}>
          <div style={{ position: "absolute", width: "100%", top: "0", borderTop: "1px solid #e2e8f0", fontSize: "9px", color: "#94a3b8", display: "flex", justifyContent: "flex-end" }}>100%</div>
          <div style={{ position: "absolute", width: "100%", top: "45px", borderTop: "1px dashed #cbd5e1" }}></div>
          <div style={{ position: "absolute", width: "100%", bottom: "20px", borderTop: "1px solid #cbd5e1" }}></div>

          <svg width="100%" height="90" viewBox="0 0 240 80" preserveAspectRatio="none" style={{ overflow: "visible" }}>
            <polyline
              fill="none"
              stroke="#4ade80"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={executionVelocity.points}
            />
            {executionVelocity.dots.map((dot, idx) => (
              <circle
                key={idx}
                cx={dot.cx}
                cy={dot.cy}
                r="4"
                fill={dot.fill}
                title={dot.title}
                style={{ transition: "all 0.3s ease" }}
              />
            ))}
          </svg>

          <div style={{ display: "flex", justifyContent: "space-between", padding: "0 10px", marginTop: "-6px", fontSize: "10px", color: "#64748b", fontWeight: "700" }}>
            {executionVelocity.labels.map((lbl, idx) => (
              <span key={idx}>{lbl}</span>
            ))}
          </div>
        </div>

        <div style={{ fontSize: "11px", color: "#64748b", fontStyle: "italic", marginTop: "10px", textAlign: "center" }}>
          "{executionVelocity.quote}"
        </div>
      </div>

      {/* Chart 2: Failure Root Causes (Tag Cloud) */}
      <div style={{ background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "12px", padding: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }} aria-label="Failure root causes tag cloud">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "800", color: "#0f172a" }}> Failure Root Causes</h4>
            <span style={{ fontSize: "11px", color: "#64748b" }}>Issue category distribution</span>
          </div>
          <span style={{ fontSize: "10px", background: "#fff1f2", color: "#9f1239", fontWeight: "800", padding: "2px 8px", borderRadius: "10px" }}>DIAGNOSTICS</span>
        </div>

        {/* Proportional Tag Cloud */}
        {failureRootCauses.items.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: "10px 16px", minHeight: "100px", padding: "10px 0" }}>
            {failureRootCauses.items.map((item, idx) => (
              <span
                key={idx}
                style={{
                  fontSize: `${item.fontSize}px`,
                  fontWeight: item.count > 1 ? "700" : "600",
                  color: item.color,
                  lineHeight: 1,
                  transition: "font-size 0.3s ease"
                }}
                title={`Count: ${item.count}`}
              >
                {item.name}
              </span>
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "100px", padding: "10px 0", color: "#166534" }}>
            <span style={{ fontSize: "15px", fontWeight: "700" }}>✓ Zero Failure Anomalies</span>
            <p style={{ fontSize: "11px", color: "#64748b", margin: "6px 0 0", textAlign: "center" }}>
              All test runs passing without selector or assertion faults
            </p>
          </div>
        )}

        <div style={{ fontSize: "11px", color: "#64748b", textAlign: "center", marginTop: "8px" }}>
          {failureRootCauses.caption}
        </div>
      </div>

      {/* Chart 3: Engine Latency (3 Small-Multiple Sparkline Trend Cards) */}
      <div style={{ background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "12px", padding: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }} aria-label="Engine latency sparklines for Chrome, Firefox, and WebKit">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "800", color: "#0f172a" }}> Engine Latency</h4>
            <span style={{ fontSize: "11px", color: "#64748b" }}>Execution duration trends</span>
          </div>
          <span style={{ fontSize: "10px", background: "#f0f9ff", color: "#0369a1", fontWeight: "800", padding: "2px 8px", borderRadius: "10px" }}>SPEED</span>
        </div>

        {/* 3-Column Sparkline Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
          <div style={{ border: "1px solid #e0f2fe", borderRadius: "8px", padding: "8px", background: "#f0f9ff" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", color: "#0284c7", display: "flex", justifyContent: "space-between" }}>
              <span> Chrome</span>
              <span>{engineLatency.chrome.avgStr}</span>
            </div>
            <svg width="100%" height="32" viewBox="0 0 100 32" preserveAspectRatio="none" style={{ marginTop: "4px" }}>
              <polyline fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={engineLatency.chrome.points} />
            </svg>
          </div>

          <div style={{ border: "1px solid #fef3c7", borderRadius: "8px", padding: "8px", background: "#fffbeb" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", color: "#d97706", display: "flex", justifyContent: "space-between" }}>
              <span>🦊 Firefox</span>
              <span>{engineLatency.firefox.avgStr}</span>
            </div>
            <svg width="100%" height="32" viewBox="0 0 100 32" preserveAspectRatio="none" style={{ marginTop: "4px" }}>
              <polyline fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={engineLatency.firefox.points} />
            </svg>
          </div>

          <div style={{ border: "1px solid #ccfbf1", borderRadius: "8px", padding: "8px", background: "#f0fdf4" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", color: "#0d9488", display: "flex", justifyContent: "space-between" }}>
              <span>🧭 WebKit</span>
              <span>{engineLatency.webkit.avgStr}</span>
            </div>
            <svg width="100%" height="32" viewBox="0 0 100 32" preserveAspectRatio="none" style={{ marginTop: "4px" }}>
              <polyline fill="none" stroke="#2dd4bf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={engineLatency.webkit.points} />
            </svg>
          </div>
        </div>

        <div style={{ fontSize: "11px", color: "#475569", marginTop: "10px", textAlign: "center", fontWeight: "600" }}>
          "{engineLatency.insight}"
        </div>
      </div>

      {/* Chart 4: Suite Automation Coverage (Segmented Horizontal Bar) */}
      <div style={{ background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "12px", padding: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }} aria-label={`Suite automation coverage ${suiteAutomation.automatedPct}% automated, ${suiteAutomation.automatedCount} automated, ${suiteAutomation.manualCount} manual, ${suiteAutomation.untestedCount} untested`}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "800", color: "#0f172a" }}> Suite Automation</h4>
            <span style={{ fontSize: "11px", color: "#64748b" }}>Automation coverage score</span>
          </div>
          <span style={{ fontSize: "10px", background: "#f5f3ff", color: "#e05300", fontWeight: "800", padding: "2px 8px", borderRadius: "10px" }}>COVERAGE</span>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: "6px", marginBottom: "10px" }}>
          <span style={{ fontSize: "28px", fontWeight: "600", color: "#a78bfa", fontFamily: "Georgia, serif" }}>{suiteAutomation.automatedPct}%</span>
          <span style={{ fontSize: "13px", fontWeight: "700", color: "#64748b" }}>automated</span>
        </div>

        <div style={{ width: "100%", height: "20px", borderRadius: "6px", overflow: "hidden", display: "flex", border: "1px solid #cbd5e1", backgroundColor: "#f8fafc", marginBottom: "12px" }}>
          <div style={{ width: `${suiteAutomation.automatedPct}%`, backgroundColor: "#c4b5fd", transition: "width 0.4s ease" }} title={`Automated: ${suiteAutomation.automatedCount}`} />
          <div style={{ width: `${suiteAutomation.manualPct}%`, backgroundColor: "#fde047", transition: "width 0.4s ease" }} title={`Manual: ${suiteAutomation.manualCount}`} />
          <div style={{ width: `${suiteAutomation.untestedPct}%`, backgroundColor: "#e2e8f0", transition: "width 0.4s ease" }} title={`Untested: ${suiteAutomation.untestedCount}`} />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", fontWeight: "700", color: "#334155" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#a78bfa" }}></span> Automated {suiteAutomation.automatedCount}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#facc15" }}></span> Manual {suiteAutomation.manualCount}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#cbd5e1" }}></span> Untested {suiteAutomation.untestedCount}
          </span>
        </div>
      </div>
    </div>
  );
}

